import { TrendingUp, TrendingDown, Users, Package, ShoppingCart, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { useLocationFilter } from '@/contexts/LocationContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ComponentType<{ className?: string }>;
  currency?: boolean;
}

const StatCard = ({ title, value, change, trend, icon: Icon, currency = false }: StatCardProps) => (
  <div className="glass-panel p-6 hover-lift">
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-accent" />
      </div>
      <div className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
        trend === 'up' ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'
      )}>
        {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {change}
      </div>
    </div>
    
    <div>
      <h3 className="text-h1 font-bold font-tabular">
        {currency && '₺'}{value}
      </h3>
      <p className="text-caption text-muted-foreground mt-1">{title}</p>
    </div>
  </div>
);

export const StatsCards = () => {
  const { companyId, loading: authLoading } = useAuth();
  const { locationFilter, currentLocationId } = useLocationFilter();

  // Today's sales query
  const { data: todaysSales = 0 } = useQuery({
    queryKey: ['todaysSales', companyId, currentLocationId],
    queryFn: async () => {
      if (!companyId) return 0;
      
      // Fetch converted onhold records to identify sales linked to onhold conversions
      const { data: convertedOnHolds } = await supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );
      
      const today = new Date().toISOString().split('T')[0];
      let query = supabase
        .from('sales')
        .select('id, total_net')
        .eq('company_id', companyId)
        .gte('sale_datetime', today)
        .in('status', ['completed', 'open']);
      
      // Apply location filter if available
      if (currentLocationId) {
        query = query.eq('location_id', currentLocationId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching today\'s sales:', error);
        return 0;
      }
      
      // Filter out 0 TL sales that are linked to onhold conversions
      const filteredSales = (data || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });
      
      return filteredSales.reduce((sum, sale) => sum + (sale.total_net || 0), 0);
    },
    enabled: !!companyId && !authLoading,
  });

  // Active customers (current visits) query
  const { data: activeCustomers = 0 } = useQuery({
    queryKey: ['activeCustomers', companyId],
    queryFn: async () => {
      if (!companyId) return 0;
      
      const { data, error } = await supabase
        .from('visits')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'PENDING_LABEL');
      
      if (error) {
        console.error('Error fetching active customers:', error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!companyId && !authLoading,
  });

  // Pending packages query
  const { data: pendingPackages = 0 } = useQuery({
    queryKey: ['pendingPackages', companyId],
    queryFn: async () => {
      if (!companyId) return 0;
      
      const { data, error } = await supabase
        .from('packages')
        .select('id')
        .eq('company_id', companyId)
        .in('status', ['draft', 'offered']);
      
      if (error) {
        console.error('Error fetching pending packages:', error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!companyId && !authLoading,
  });

  // Labeled visits query
  const { data: labeledVisits = 0 } = useQuery({
    queryKey: ['labeledVisits', companyId],
    queryFn: async () => {
      if (!companyId) return 0;
      
      const { data, error } = await supabase
        .from('visits')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'LABELED');
      
      if (error) {
        console.error('Error fetching labeled visits:', error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!companyId && !authLoading,
  });

  // Previous day's sales for comparison
  const { data: yesterdaysSales = 0 } = useQuery({
    queryKey: ['yesterdaysSales', companyId, currentLocationId],
    queryFn: async () => {
      if (!companyId) return 0;
      
      // Fetch converted onhold records to identify sales linked to onhold conversions
      const { data: convertedOnHolds } = await supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );
      
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      let yQuery = supabase
        .from('sales')
        .select('id, total_net')
        .eq('company_id', companyId)
        .gte('sale_datetime', yesterdayStr)
        .lt('sale_datetime', new Date().toISOString().split('T')[0])
        .in('status', ['completed', 'open']);

      if (currentLocationId) {
        yQuery = yQuery.eq('location_id', currentLocationId);
      }

      const { data, error } = await yQuery;
      
      if (error) {
        console.error('Error fetching yesterday\'s sales:', error);
        return 0;
      }
      
      // Filter out 0 TL sales that are linked to onhold conversions
      const filteredSales = (data || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });
      
      return filteredSales.reduce((sum, sale) => sum + (sale.total_net || 0), 0);
    },
    enabled: !!companyId && !authLoading,
  });

  if (authLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="w-16 h-6 rounded-full" />
            </div>
            <Skeleton className="w-20 h-8 mb-2" />
            <Skeleton className="w-24 h-4" />
          </div>
        ))}
      </div>
    );
  }

  // Calculate percentage changes
  const salesChange = yesterdaysSales > 0 ? 
    ((todaysSales - yesterdaysSales) / yesterdaysSales * 100).toFixed(0) : 
    todaysSales > 0 ? '+100' : '0';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const stats = [
    {
      title: 'Bugünkü Satış',
      value: formatCurrency(todaysSales),
      change: `${salesChange}%`,
      trend: (todaysSales >= yesterdaysSales ? 'up' : 'down') as const,
      icon: ShoppingCart,
      currency: true,
    },
    {
      title: 'Aktif Müşteriler',
      value: activeCustomers.toString(),
      change: `${activeCustomers}`,
      trend: 'up' as const,
      icon: Users,
    },
    {
      title: 'Bekleyen Paketler',
      value: pendingPackages.toString(),
      change: `${pendingPackages}`,
      trend: pendingPackages > 5 ? 'up' : 'down' as const,
      icon: Package,
    },
    {
      title: 'Etiketli Ziyaret',
      value: labeledVisits.toString(),
      change: `${labeledVisits}`,
      trend: 'up' as const,
      icon: UserCheck,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};